/**
 * Tour geometry — the arithmetic behind the spotlight, with no DOM in it.
 *
 * Ported from design/Clinic Stock v2.dc.html:4228 (`measureTour`) and :6104
 * (the card placement). Separated from the React component on purpose: this is
 * the part with actual logic and edge cases, and a `getBoundingClientRect` in
 * the middle of it would make every case need a browser to test.
 *
 * The prototype is the oracle. Where a number here looks arbitrary — 12, 18,
 * 342, 260 — it is the prototype's number and the tests assert it.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/** Card dimensions from the prototype. 342 wide is the design; 260 is an assumed height. */
export const CARD_WIDTH = 342;
export const CARD_HEIGHT = 260;
/** Gap between the spotlight and the card. */
export const CARD_MARGIN = 18;
/** Minimum distance from any viewport edge. */
export const EDGE = 12;
/** A spotlight never shrinks below this, so a thin row is still visibly ringed. */
export const MIN_SPOT_HEIGHT = 40;

/**
 * Clamps a measured element rect into the viewport.
 *
 * A tall element — an inventory table with forty rows — extends past the fold,
 * and a spotlight matching its true height would ring something mostly
 * offscreen, dimming the entire visible page. Clipping to the viewport keeps
 * the highlight on the part the reader can actually see.
 */
export function clampToViewport(
  rect: { top: number; left: number; width: number; height: number },
  viewport: Viewport,
): Rect {
  const y = Math.max(rect.top, EDGE);
  const bottom = Math.min(rect.top + rect.height, viewport.height - EDGE);
  return {
    x: Math.round(rect.left),
    y: Math.round(y),
    w: Math.round(rect.width),
    h: Math.round(Math.max(MIN_SPOT_HEIGHT, bottom - y)),
  };
}

export type CardSide = 'right' | 'left' | 'below' | 'above';

export interface CardPlacement {
  left: number;
  top: number;
  side: CardSide;
}

/**
 * Places the tour card beside the spotlight, trying right → left → below →
 * above, then clamping to the viewport.
 *
 * The order is not arbitrary. Right first because the sidebar occupies the left
 * of every screen, so a card on the right of a highlighted element is almost
 * always over empty space. Above is last because a card that appears above its
 * target reads as belonging to whatever is above it.
 *
 * The final clamp can pull the card back over the spotlight on a small screen.
 * That is accepted deliberately: a card half offscreen is unreadable, whereas
 * one overlapping a highlight is merely untidy.
 */
export function placeCard(
  spot: Rect,
  viewport: Viewport,
  card: { width: number; height: number } = { width: CARD_WIDTH, height: CARD_HEIGHT },
): CardPlacement {
  let left: number;
  let top: number;
  let side: CardSide;

  if (spot.x + spot.w + CARD_MARGIN + card.width <= viewport.width - EDGE) {
    left = spot.x + spot.w + CARD_MARGIN;
    top = spot.y;
    side = 'right';
  } else if (spot.x - CARD_MARGIN - card.width >= EDGE) {
    left = spot.x - CARD_MARGIN - card.width;
    top = spot.y;
    side = 'left';
  } else if (spot.y + spot.h + CARD_MARGIN + card.height <= viewport.height - EDGE) {
    left = spot.x + 24;
    top = spot.y + spot.h + CARD_MARGIN;
    side = 'below';
  } else {
    left = spot.x + 24;
    top = spot.y - CARD_MARGIN - card.height;
    side = 'above';
  }

  return {
    left: Math.max(EDGE, Math.min(left, viewport.width - card.width - EDGE)),
    top: Math.max(EDGE, Math.min(top, viewport.height - card.height - EDGE)),
    side,
  };
}

/**
 * Where the window should scroll to put an element comfortably in view.
 *
 * The prototype scrolls a container; this app scrolls the window, because the
 * sidebar is `position: sticky` and the page itself is the scroller. Same
 * intent: centre the element vertically unless it is taller than the viewport,
 * in which case put its top near the top with a margin.
 *
 * Returns null when the element is already close enough — scrolling by three
 * pixels is a visible jolt for no benefit, and it retriggers the measure.
 */
export function scrollTargetFor(
  rect: { top: number; height: number },
  currentScrollY: number,
  viewport: Viewport,
  threshold = 4,
): number | null {
  const usable = Math.min(rect.height, viewport.height - 48);
  const want =
    currentScrollY + rect.top - Math.max(24, (viewport.height - usable) / 2);
  const target = Math.max(0, want);
  return Math.abs(target - currentScrollY) > threshold ? target : null;
}

/** True when two rects differ enough to be worth re-rendering. */
export function rectChanged(a: Rect | null, b: Rect): boolean {
  return !a || a.x !== b.x || a.y !== b.y || a.w !== b.w || a.h !== b.h;
}
